import type { CompetitorSourceName, LeFlashStatus } from '~/types/services/priceParrot';

export const COMPETITOR_OFFERS_PATH = '/marketing/competitor-offers';
export const CUSTOMER_DEMAND_PATH = '/marketing/customer-demand-report';

export const COMPETITOR_SOURCE_LABELS: Record<CompetitorSourceName, string> = {
  secret_escapes: 'Secret Escapes',
  travelzoo: 'Travelzoo',
};

export const COMPETITOR_OFFERS_PAGE_SIZE = 25;

export const HISTORY_COLLAPSED_ROWS = 2;
export const HISTORY_PAGE_SIZE = 10;

// svc-bedbank's auto-verify tiers; 3–5 are suggestions to check.
export const AUTO_VERIFIED_MAX_MATCH_TIER = 2;

// From lux-analytics competitor_collector_property_match: name similarity and distance to the flash property.
export const MATCH_TIER_DESCRIPTIONS: Record<number, string> = {
  1: 'Identical hotel name, within 200m',
  2: 'Hotel name at least 90% similar, within 50m',
  3: 'Hotel name at least 85% similar, within 100m',
  4: 'Hotel name at least 75% similar, within 200m',
  5: 'Hotel name at least 65% similar, within 200m',
};

export const DATE_FORMAT = 'D MMM YYYY';

// The default tooltip is a translucent grey in both modes; a paper surface follows the theme.
export const PAPER_TOOLTIP_SX = {
  maxWidth: 420,
  p: 1,
  bgcolor: 'background.paper',
  color: 'text.primary',
  border: 1,
  borderColor: 'divider',
  boxShadow: 4,
};

// Countries named in the Searching from cell; its hover table lists all the API returns.
export const SEARCHING_FROM_CELL_COUNTRIES = 3;

// lux-analytics keeps 100 days of GHA opportunities.
export const OPPORTUNITY_HISTORY_DAYS = 99;
export const DATE_TIME_FORMAT = 'D MMM YYYY, HH:mm';

export const COMPETITOR_SOURCE_NAMES = Object.keys(COMPETITOR_SOURCE_LABELS) as Array<CompetitorSourceName>;

// Collected once a day, so the last collection is as current as we know; the live site may differ.
export enum CompetitorFlashStatus {
  IN_LAST_COLLECTION = 'in_last_collection',
  NOT_IN_LAST_COLLECTION = 'not_in_last_collection',
}

export const COMPETITOR_FLASH_STATUS_LABELS: Record<CompetitorFlashStatus, string> = {
  [CompetitorFlashStatus.IN_LAST_COLLECTION]: 'In last collection',
  [CompetitorFlashStatus.NOT_IN_LAST_COLLECTION]: 'Not in last collection',
};

// Matching covers every Flash property we've had, live or ended, so No means we've likely never run Flash there.
export enum FlashMatch {
  YES = 'yes',
  NO = 'no',
}

export const FLASH_MATCH_LABELS: Record<FlashMatch, string> = {
  [FlashMatch.YES]: 'Yes',
  [FlashMatch.NO]: 'No',
};

export const LE_FLASH_STATUS_LABELS: Record<LeFlashStatus, string> = {
  now: 'Now',
  upcoming: 'Upcoming',
  before: 'Before',
  never: 'Never',
};

export enum CompetitorOffersPreset {
  FIND_OPPORTUNITIES = 'find_opportunities',
  COMPARE_OFFERS = 'compare_offers',
}

export const COMPETITOR_OFFERS_PRESETS: Array<{
  value: CompetitorOffersPreset;
  label: string;
  description: string;
  onCompetitor: CompetitorFlashStatus;
  onLe: LeFlashStatus;
}> = [
  {
    value: CompetitorOffersPreset.FIND_OPPORTUNITIES,
    label: 'Find opportunities',
    description: 'Competitors have Flash at these hotels, and we’ve never run Flash with them.',
    onCompetitor: CompetitorFlashStatus.IN_LAST_COLLECTION,
    onLe: 'never',
  },
  {
    value: CompetitorOffersPreset.COMPARE_OFFERS,
    label: 'Compare offers',
    description: 'We and at least one competitor have Flash on sale. Open Details to compare rooms.',
    onCompetitor: CompetitorFlashStatus.IN_LAST_COLLECTION,
    onLe: 'now',
  },
];

// A published sale date further than this from today is a placeholder, not a real sale window.
export const SALE_DATE_PLACEHOLDER_DAYS = 365;

export enum TimelineZoom {
  ALL = 'all',
  WEEK = 'week',
  MONTH = 'month',
  QUARTER = 'quarter',
}

export const TIMELINE_ZOOM_OPTIONS = [
  { value: TimelineZoom.ALL, label: 'Full range' },
  { value: TimelineZoom.WEEK, label: 'Week' },
  { value: TimelineZoom.MONTH, label: 'Month' },
  { value: TimelineZoom.QUARTER, label: 'Quarter' },
];
